import React, { useEffect, useRef, useState } from 'react'
import { CameraIcon, ReceiptIcon, UploadIcon } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { fetchJobTimeEntries } from '../../api/jobRelatedRecords'
import {
  fetchJobAttachments,
  fetchJobTasks,
  uploadJobFile,
  type JobAttachment,
  type JobCaptureSource,
} from '../../api/jobWorkflow'
import { useAppSettings } from '../../hooks/useAppSettings'
import { usePermissions } from '../../hooks/usePermissions'
import { jobMeetsAllPhotoRequirements } from '../../lib/jobPhotoPolicy'
import {
  fetchSummedJobActualHours,
  hoursActualToPersist,
  sumJobTimeEntryHours,
} from '../../lib/jobActualHours'
import { isDsnyEveningJob } from '../../lib/routeOptimizer'
import { parseClearAndBlackBags } from '../../lib/garbageBags'
import { snapshotGarbageBagsCost } from '../../api/garbageBagCosts'
import { JobExpenseQuickForm } from './JobExpenseQuickForm'

export interface CompletionLineItem {
  id: string
  description: string
  is_variable: boolean
  quantity: number
  unit_price: number
  actual_quantity: number
}

interface CompletionApartment {
  id: string
  job_line_item_id: string
  unit_number_snapshot: string
  tier_description_snapshot: string
  outcome: 'completed' | 'carry_forward'
  taskDone: number
  taskTotal: number
}

interface JobCompletionModalProps {
  jobId: string
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
  /** Crew / field home: hide hours & materials; auto-set hours from time_entries. */
  autoHours?: boolean
}

export function JobCompletionModal({
  jobId,
  isOpen,
  onClose,
  onSaved,
  autoHours = false,
}: JobCompletionModalProps) {
  const { can } = usePermissions()
  const canAttachFiles = can('jobs.edit')
  const canSubmitExpenses = can('expenses.submit')
  const [lineItems, setLineItems] = useState<CompletionLineItem[]>([])
  const [hoursActual, setHoursActual] = useState<number>(0)
  const [materialsCost, setMaterialsCost] = useState<number>(0)
  const [saving, setSaving] = useState(false)
  const [photos, setPhotos] = useState<JobAttachment[]>([])
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [apartments, setApartments] = useState<CompletionApartment[]>([])
  const [isDsnyStop, setIsDsnyStop] = useState(false)
  const [clearBags, setClearBags] = useState('')
  const [blackBags, setBlackBags] = useState('')
  const [bagError, setBagError] = useState<string | null>(null)
  const [showExpenses, setShowExpenses] = useState(false)
  const cameraRef = useRef<HTMLInputElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const { photoPolicy } = useAppSettings()
  const cameraOnly = photoPolicy === 'camera_required'

  const loadPhotos = async () => {
    const all = await fetchJobAttachments(jobId)
    setPhotos(all.filter((a) => a.attachment_type === 'photo'))
  }

  useEffect(() => {
    if (!isOpen) return
    const load = async () => {
      const { data: items } = await supabase
        .from('job_line_items')
        .select('id, description, is_variable, quantity, unit_price, actual_quantity')
        .eq('job_id', jobId)
      setLineItems(
        (items || []).map((i) => ({
          id: i.id,
          description: i.description,
          is_variable: i.is_variable,
          quantity: Number(i.quantity || 0),
          unit_price: Number(i.unit_price || 0),
          actual_quantity: Number(i.actual_quantity ?? i.quantity ?? 0),
        }))
      )
      const [{ data: unitRows }, taskRows] = await Promise.all([
        supabase
          .from('job_line_item_units')
          .select(
            'id, job_line_item_id, unit_number_snapshot, tier_description_snapshot, completion_status'
          )
          .eq('job_id', jobId)
          .order('created_at'),
        fetchJobTasks(jobId),
      ])
      const apartmentRows: CompletionApartment[] = (unitRows || []).map((unit) => {
        const unitTasks = taskRows.filter((task) => task.job_line_item_unit_id === unit.id)
        const taskDone = unitTasks.filter((task) => task.is_done).length
        return {
          id: unit.id,
          job_line_item_id: unit.job_line_item_id,
          unit_number_snapshot: unit.unit_number_snapshot,
          tier_description_snapshot: unit.tier_description_snapshot,
          outcome:
            unit.completion_status === 'completed' ||
            (unitTasks.length > 0 && taskDone === unitTasks.length)
              ? 'completed'
              : 'carry_forward',
          taskDone,
          taskTotal: unitTasks.length,
        }
      })
      setApartments(apartmentRows)
      if (apartmentRows.length > 0) {
        setLineItems((current) =>
          current.map((line) => {
            const completedQuantity = apartmentRows.filter(
              (unit) => unit.job_line_item_id === line.id && unit.outcome === 'completed'
            ).length
            return { ...line, actual_quantity: completedQuantity }
          })
        )
      }
      const entries = await fetchJobTimeEntries(jobId)
      const fromEntries = sumJobTimeEntryHours(entries)
      const { data: job } = await supabase
        .from('jobs')
        .select(
          'hours_actual, materials_cost, title, service_type, dsny_integration, dsny_pickup_days, garbage_bags_clear, garbage_bags_black'
        )
        .eq('id', jobId)
        .single()
      const storedHours = Number(job?.hours_actual || 0)
      setHoursActual(autoHours || storedHours <= 0 ? fromEntries : storedHours)
      if (job?.materials_cost) setMaterialsCost(Number(job.materials_cost))
      const dsny = isDsnyEveningJob({
        title: job?.title,
        serviceType: job?.service_type,
        dsnyIntegration: job?.dsny_integration,
        dsnyPickupDays: job?.dsny_pickup_days,
      })
      setIsDsnyStop(dsny)
      setClearBags(
        job?.garbage_bags_clear != null && Number(job.garbage_bags_clear) >= 0
          ? String(job.garbage_bags_clear)
          : ''
      )
      setBlackBags(
        job?.garbage_bags_black != null && Number(job.garbage_bags_black) >= 0
          ? String(job.garbage_bags_black)
          : ''
      )
      setBagError(null)
      setShowExpenses(false)
      await loadPhotos()
      setPhotoError(null)
    }
    load()
  }, [isOpen, jobId, autoHours])

  if (!isOpen) return null

  /** Office only: skip entering hours/materials. Field uses Enter expenses + Complete. */
  const canSkipBillables = !autoHours && apartments.length === 0
  const showDsnyBags = autoHours && isDsnyStop

  const computeTotal = (hours = hoursActual) => {
    let total = autoHours ? 0 : materialsCost
    const apartmentLineIds = new Set(apartments.map((unit) => unit.job_line_item_id))
    for (const li of lineItems) {
      const qty =
        li.is_variable || apartmentLineIds.has(li.id) ? li.actual_quantity : li.quantity
      total += qty * li.unit_price
    }
    if (hours > 0) {
      const hourly = lineItems.find((l) => l.description === 'Labor')
      if (hourly) total += hours * hourly.unit_price
    }
    return total
  }

  const ensurePhotoPolicy = async () => {
    const result = await jobMeetsAllPhotoRequirements(jobId, photoPolicy)
    if (!result.ok) {
      setPhotoError(result.message || 'Photo required')
      return false
    }
    setPhotoError(null)
    return true
  }

  const handleUpload = async (files: FileList | null, captureSource: JobCaptureSource) => {
    if (!files?.length) return
    setUploading(true)
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) continue
        await uploadJobFile(jobId, file, 'photo', undefined, captureSource)
      }
      await loadPhotos()
      setPhotoError(null)
    } catch (e) {
      alert('Photo upload failed: ' + (e as Error).message)
    } finally {
      setUploading(false)
      if (cameraRef.current) cameraRef.current.value = ''
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const handleSave = async () => {
    if (!(await ensurePhotoPolicy())) return
    let bagsToWrite: { clear: number; black: number } | null = null
    if (showDsnyBags) {
      const bags = parseClearAndBlackBags(clearBags, blackBags)
      if (!bags.ok) {
        setBagError(bags.message)
        return
      }
      bagsToWrite = { clear: bags.clear, black: bags.black }
      setBagError(null)
    }
    setSaving(true)
    try {
      if (apartments.length > 0) {
        const { error } = await supabase.rpc('complete_turnover_job_units', {
          p_job_id: jobId,
          p_completed_unit_ids: apartments
            .filter((unit) => unit.outcome === 'completed')
            .map((unit) => unit.id),
          p_carry_forward_unit_ids: apartments
            .filter((unit) => unit.outcome === 'carry_forward')
            .map((unit) => unit.id),
        })
        if (error) throw error
      }
      for (const li of lineItems) {
        if (li.is_variable) {
          await supabase
            .from('job_line_items')
            .update({
              actual_quantity: li.actual_quantity,
              total_price: li.actual_quantity * li.unit_price,
            })
            .eq('id', li.id)
        }
      }
      const computedHours = await fetchSummedJobActualHours(jobId)
      const hoursToWrite = hoursActualToPersist(computedHours, hoursActual, autoHours)
      const total = computeTotal(hoursToWrite ?? hoursActual)
      const update: Record<string, unknown> = {
        actual_amount: total,
        total_billable_amount: total,
      }
      if (hoursToWrite != null) update.hours_actual = hoursToWrite
      if (!autoHours) update.materials_cost = materialsCost || null
      if (bagsToWrite != null) {
        update.garbage_bags_clear = bagsToWrite.clear
        update.garbage_bags_black = bagsToWrite.black
        update.garbage_bags_cost = await snapshotGarbageBagsCost(
          bagsToWrite.clear,
          bagsToWrite.black
        )
      }
      await supabase.from('jobs').update(update).eq('id', jobId)
      onSaved()
      onClose()
    } catch (e) {
      alert('Failed to save: ' + (e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const handleSkip = async () => {
    if (!(await ensurePhotoPolicy())) return
    onSaved()
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {autoHours ? 'Complete job' : 'Confirm billables'}
        </h3>
        {autoHours && (
          <p className="text-sm text-gray-600 mb-4">
            Hours are taken from job punches. Billing is handled by the office.
          </p>
        )}

        <div className="mb-4 border border-gray-200 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-800">Completion photos</p>
            <p className="text-xs text-gray-500">{photos.length} photo{photos.length === 1 ? '' : 's'}</p>
          </div>
          {photos.length > 0 && (
            <div className="flex gap-2 overflow-x-auto">
              {photos.slice(0, 8).map((p) => (
                <img
                  key={p.id}
                  src={p.file_url}
                  alt=""
                  className="w-14 h-14 object-cover rounded border border-gray-200"
                />
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files, 'camera')}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => cameraRef.current?.click()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <CameraIcon className="w-4 h-4" />
              Take Photo
            </button>
            {!cameraOnly && canAttachFiles && (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleUpload(e.target.files, 'upload')}
                />
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  <UploadIcon className="w-4 h-4" />
                  Attach
                </button>
              </>
            )}
          </div>
          {photoError && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
              {photoError}
            </p>
          )}
        </div>

        {lineItems.filter((l) => l.is_variable).length > 0 && (
          <div className="space-y-3 mb-4">
            <p className="text-sm text-gray-600">Enter actual quantities for variable line items:</p>
            {lineItems
              .filter((l) => l.is_variable)
              .map((li) => (
                <div key={li.id}>
                  <label className="block text-sm font-medium text-gray-700">{li.description}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={li.actual_quantity}
                    onChange={(e) =>
                      setLineItems((prev) =>
                        prev.map((p) =>
                          p.id === li.id
                            ? { ...p, actual_quantity: parseFloat(e.target.value) || 0 }
                            : p
                        )
                      )
                    }
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <p className="text-xs text-gray-500">@ ${li.unit_price.toFixed(2)}</p>
                </div>
              ))}
          </div>
        )}
        {apartments.length > 0 && (
          <div className="space-y-3 mb-4">
            <div>
              <p className="text-sm font-medium text-gray-900">Apartment outcomes</p>
              <p className="text-xs text-gray-500">
                Unfinished apartments will move to a new unscheduled, unassigned job.
              </p>
            </div>
            {apartments.map((apartment) => (
              <div
                key={apartment.id}
                className="rounded-lg border border-gray-200 p-3 flex flex-wrap items-center justify-between gap-3"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Apartment {apartment.unit_number_snapshot}
                  </p>
                  <p className="text-xs text-gray-500">
                    {apartment.tier_description_snapshot} · {apartment.taskDone}/
                    {apartment.taskTotal} tasks
                  </p>
                </div>
                <select
                  value={apartment.outcome}
                  onChange={(event) => {
                    const outcome = event.target.value as CompletionApartment['outcome']
                    setApartments((current) =>
                      current.map((unit) =>
                        unit.id === apartment.id ? { ...unit, outcome } : unit
                      )
                    )
                    setLineItems((current) =>
                      current.map((line) =>
                        line.id === apartment.job_line_item_id
                          ? {
                              ...line,
                              actual_quantity: apartments.filter(
                                (unit) =>
                                  unit.job_line_item_id === line.id &&
                                  (unit.id === apartment.id
                                    ? outcome === 'completed'
                                    : unit.outcome === 'completed')
                              ).length,
                            }
                          : line
                      )
                    )
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="completed">Completed</option>
                  <option value="carry_forward">Not finished — carry forward</option>
                </select>
              </div>
            ))}
          </div>
        )}
        {!autoHours && (
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Hours actual</label>
              <input
                type="number"
                min="0"
                step="0.25"
                value={hoursActual}
                onChange={(e) => setHoursActual(parseFloat(e.target.value) || 0)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Materials cost</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={materialsCost}
                onChange={(e) => setMaterialsCost(parseFloat(e.target.value) || 0)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>
        )}
        {showDsnyBags && (
          <div className="mb-4 space-y-3">
            <div>
              <p className="text-sm font-medium text-gray-900">Garbage bags used</p>
              <p className="text-xs text-gray-500 mt-0.5">Required for DSNY stops (enter 0 if none)</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Black bags <span className="text-red-600">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  value={blackBags}
                  onChange={(e) => {
                    setBlackBags(e.target.value)
                    setBagError(null)
                  }}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Clear bags <span className="text-red-600">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  value={clearBags}
                  onChange={(e) => {
                    setClearBags(e.target.value)
                    setBagError(null)
                  }}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="0"
                />
              </div>
            </div>
            {bagError && <p className="text-sm text-red-700">{bagError}</p>}
          </div>
        )}
        {autoHours ? (
          <p className="text-sm font-semibold text-gray-900 mb-4">
            Hours from punches:{' '}
            {hoursActual > 0 ? `${hoursActual.toFixed(2)}` : 'None yet'}
          </p>
        ) : (
          <p className="text-sm font-semibold text-gray-900 mb-4">
            Total billable: ${computeTotal().toFixed(2)}
          </p>
        )}
        {autoHours && showExpenses && canSubmitExpenses && (
          <JobExpenseQuickForm
            jobId={jobId}
            onCancel={() => setShowExpenses(false)}
          />
        )}
        <div className={`flex ${autoHours ? 'flex-col sm:flex-row' : ''} justify-end gap-2`}>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            Cancel
          </button>
          {autoHours && canSubmitExpenses && (
            <button
              type="button"
              onClick={() => setShowExpenses((v) => !v)}
              className="px-4 py-2 border border-gray-300 rounded-lg inline-flex items-center justify-center gap-1.5"
            >
              <ReceiptIcon className="w-4 h-4" />
              {showExpenses ? 'Hide expenses' : 'Enter expenses'}
            </button>
          )}
          {canSkipBillables && (
            <button
              type="button"
              onClick={handleSkip}
              className="px-4 py-2 border border-gray-300 rounded-lg"
            >
              Skip billables
            </button>
          )}
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : autoHours ? 'Complete job' : 'Save & complete'}
          </button>
        </div>
      </div>
    </div>
  )
}
